import { useEffect, useMemo, useRef } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { listDeploymentJobs, listJobs, type DeploymentJob } from '../services/api'
import { isTerminalStatus, jobDisplayName, type PackagingJob } from '../types/job'
import { useAuthStore } from '../store/authStore'
import { intuneKeys } from './useIntuneQuery'
import { useTaskListStore } from '../store/taskListStore'
import { jobTitle } from '../components/DeploymentJobsPanel'
import { errorText } from '../utils/errorText'

/**
 * The tenant's background work, read once for the whole session.
 *
 * Two server-side job kinds feed it: deployment runs (`GET /deployments/jobs`) and packaging jobs
 * (`GET /jobs`). The header mounts this hook so both are polled while the portal is open; a page
 * that shows the same data reads the same query and costs no second request.
 */

/** Cache keys for the two job lists. The tenant goes LAST, so an
 *  `invalidateQueries({ queryKey: taskKeys.deploymentJobs() })` elsewhere still matches by prefix,
 *  and one tenant's runs are never served to the next after a tenant switch. */
export const taskKeys = {
  deploymentJobs: () => ['deployment-jobs'] as const,
  packagingJobs: () => ['packaging-jobs', 'recent'] as const,
}

/** The exact keys, for `setQueryData`, which does not match by prefix. */
export const deploymentJobsKey = (tenantId: string | null) =>
  [...taskKeys.deploymentJobs(), { tenant: tenantId }] as const
export const packagingJobsKey = (tenantId: string | null) =>
  [...taskKeys.packagingJobs(), { tenant: tenantId }] as const

/** Polling cadence: fast while something is being watched, slow while nothing is. */
export const RUNNING_POLL_MS = 5_000
export const IDLE_POLL_MS = 30_000

/** How many finished, undismissed tasks the list shows. Older ones are on their pages. */
export const FINISHED_SHOWN = 20

/** How many recent packaging jobs are read for the finished half of the list. */
export const PACKAGING_RECENT_LIMIT = 20

/** The running packaging jobs are read on their own, at the endpoint's ceiling: a batch of
 *  thirty submitted from Discovery runs oldest first, and the newest twenty would hold none of
 *  the ones in progress. */
export const PACKAGING_RUNNING_LIMIT = 100

export const PACKAGING_RUNNING_STATUSES = ['pending', 'queued', 'processing'] as const

/** Every running packaging job plus the recent ones, merged by id, newest first. */
export async function listTaskPackagingJobs(): Promise<{ jobs: PackagingJob[] }> {
  const [running, recent] = await Promise.all([
    listJobs({ status: [...PACKAGING_RUNNING_STATUSES], limit: PACKAGING_RUNNING_LIMIT }),
    listJobs({ limit: PACKAGING_RECENT_LIMIT }),
  ])
  const byId = new Map<string, PackagingJob>()
  for (const job of [...running.jobs, ...recent.jobs]) byId.set(job.job_id, job)
  return { jobs: [...byId.values()] }
}

export type TaskState = 'running' | 'succeeded' | 'failed' | 'attention' | 'cancelled'

interface TaskBase {
  id: string
  title: string
  state: TaskState
  /** When the task was recorded; what "newest first" orders by. */
  createdAt: string | null
}

export type Task =
  | (TaskBase & { kind: 'deployment'; job: DeploymentJob })
  | (TaskBase & { kind: 'packaging'; progressPct: number; job: PackagingJob })

export const isRunningDeployment = (job: DeploymentJob) =>
  job.status === 'queued' || job.status === 'processing'

export const isRunningPackaging = (job: PackagingJob) => !isTerminalStatus(job.status)

/** A run's state as a task. `completed` with a failed item is NOT a success — an item needing
 *  attention is neither, and is kept apart because retrying it is what creates a duplicate. */
export function deploymentTask(job: DeploymentJob): Task {
  let state: TaskState
  if (isRunningDeployment(job)) state = 'running'
  else if (job.needs_attention > 0) state = 'attention'
  else if (job.status === 'failed' || job.failed > 0) state = 'failed'
  else state = 'succeeded'
  return { kind: 'deployment', id: job.id, title: jobTitle(job), state, createdAt: job.created_at ?? null, job }
}

export function packagingTask(job: PackagingJob): Task {
  let state: TaskState
  if (isRunningPackaging(job)) state = 'running'
  else if (job.status === 'cancelled') state = 'cancelled'
  else if (job.status === 'failed') state = 'failed'
  else state = 'succeeded'
  return {
    kind: 'packaging',
    id: job.job_id,
    title: jobDisplayName(job),
    state,
    createdAt: job.created_at ?? null,
    progressPct: job.progress_pct ?? 0,
    job,
  }
}

const createdMs = (t: Task) => (t.createdAt ? new Date(t.createdAt).getTime() : 0)

/** Running first, then newest first. */
export function orderTasks(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const ra = a.state === 'running' ? 0 : 1
    const rb = b.state === 'running' ? 0 : 1
    if (ra !== rb) return ra - rb
    return createdMs(b) - createdMs(a)
  })
}

export interface TaskListView {
  /** What the list shows: every running task, then the newest finished ones not yet dismissed. */
  tasks: Task[]
  running: Task[]
  finished: Task[]
  /** Finished, undismissed tasks beyond what is shown. */
  hiddenCount: number
}

export function deriveTasks(
  deployments: DeploymentJob[] | undefined,
  packaging: PackagingJob[] | undefined,
  dismissed: ReadonlySet<string>,
): TaskListView {
  const all = orderTasks([
    ...(deployments ?? []).map(deploymentTask),
    ...(packaging ?? []).map(packagingTask),
  ])
  const running = all.filter((t) => t.state === 'running')
  const finishedAll = all.filter((t) => t.state !== 'running' && !dismissed.has(t.id))
  const finished = finishedAll.slice(0, FINISHED_SHOWN)
  return {
    tasks: [...running, ...finished],
    running,
    finished,
    hiddenCount: finishedAll.length - finished.length,
  }
}

/** Whether either list, as last read, holds a running task. Read from the cache rather than a
 *  closure so both queries speed up together: a deployment starting must not leave the packaging
 *  list on its idle cadence, or a packaging job started meanwhile is half a minute late. */
const anyRunning = (queryClient: QueryClient, tenantId: string | null): boolean => {
  const d = queryClient.getQueryData<{ deployments: DeploymentJob[] }>(deploymentJobsKey(tenantId))
  const p = queryClient.getQueryData<{ jobs: PackagingJob[] }>(packagingJobsKey(tenantId))
  return (
    (d?.deployments ?? []).some(isRunningDeployment) || (p?.jobs ?? []).some(isRunningPackaging)
  )
}

/** Runs `effect` each time `seq` moves past the value it had on mount — never on mount itself,
 *  so a page opening does not re-read a list it has just read. */
function useOnAdvance(seq: number, effect: () => void) {
  const seen = useRef(seq)
  useEffect(() => {
    if (seq === seen.current) return
    seen.current = seq
    effect()
    // `effect` is a fresh closure each render; only the counter decides whether it runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq])
}

/** What each run had reached when the list was last read, per tenant: its status and the items
 *  whose Intune app exists. Module-level so that the several components reading the same list
 *  react to a change once between them, not once each. */
const seenRuns = new Map<string | null, Map<string, { finished: boolean; apps: Set<string> }>>()

const isFinishedRun = (job: DeploymentJob) => job.status === 'completed' || job.status === 'failed'

/** True when a run the previous read already had has since finished or created an Intune app:
 *  the deployment lists and details no longer match Intune. Records what this read shows. */
function runsAdvanced(tenantId: string | null, jobs: DeploymentJob[] | undefined): boolean {
  const prev = seenRuns.get(tenantId)
  const next = new Map((jobs ?? []).map((job) => [job.id, {
    finished: isFinishedRun(job),
    apps: new Set((job.items ?? []).filter((i) => i.intune_app_id).map((i) => i.id)),
  }]))
  seenRuns.set(tenantId, next)
  if (!prev) return false
  for (const [id, now] of next) {
    const before = prev.get(id)
    if (!before) continue
    if (now.finished && !before.finished) return true
    for (const item of now.apps) if (!before.apps.has(item)) return true
  }
  return false
}

/** The deployment-runs half on its own, for a page that only shows runs. Same key, same cadence,
 *  so the header and the page share one request. Whether to read at all is the caller's: the
 *  deployments page sits behind its own route guard and reads unconditionally. */
export function useDeploymentJobsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const tenantId = useAuthStore((s) => s.tenantContext?.tenant_id ?? null)
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: deploymentJobsKey(tenantId),
    queryFn: listDeploymentJobs,
    enabled,
    refetchInterval: () => (anyRunning(queryClient, tenantId) ? RUNNING_POLL_MS : IDLE_POLL_MS),
    retry: false,
  })

  // A run this browser just recorded goes into the cached list at once, in front, and the list is
  // re-read behind it. Seeding is what makes "View progress" on the handoff banner show the run
  // instead of an empty list for the length of a round trip.
  const startSeq = useTaskListStore((s) => s.deploymentStartSeq)
  useOnAdvance(startSeq, () => {
    const job = useTaskListStore.getState().startedDeployment
    if (job) {
      queryClient.setQueryData<{ deployments: DeploymentJob[] }>(deploymentJobsKey(tenantId), (prev) =>
        prev ? { deployments: [job, ...prev.deployments.filter((j) => j.id !== job.id)] } : prev,
      )
    }
    queryClient.invalidateQueries({ queryKey: deploymentJobsKey(tenantId) })
  })

  // A run finishing, or publishing an app, changes what the deployment lists and details show,
  // so they are re-read then rather than on their own slower cadence.
  useEffect(() => {
    if (!query.data) return
    if (runsAdvanced(tenantId, query.data.deployments)) {
      queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })
      // Every deployment's details, by prefix.
      queryClient.invalidateQueries({ queryKey: [intuneKeys.deploymentDetails(null)[0]] })
    }
  }, [query.data, tenantId, queryClient])

  return query
}

export function usePackagingJobsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const tenantId = useAuthStore((s) => s.tenantContext?.tenant_id ?? null)
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: packagingJobsKey(tenantId),
    queryFn: listTaskPackagingJobs,
    enabled,
    refetchInterval: () => (anyRunning(queryClient, tenantId) ? RUNNING_POLL_MS : IDLE_POLL_MS),
    retry: false,
  })

  const startSeq = useTaskListStore((s) => s.packagingStartSeq)
  useOnAdvance(startSeq, () => {
    queryClient.invalidateQueries({ queryKey: packagingJobsKey(tenantId) })
  })

  return query
}

export function useTaskList() {
  const tenantId = useAuthStore((s) => s.tenantContext?.tenant_id ?? null)
  // Each half is read only for a user permitted to see it; the backend already enforces the
  // same, so the gate decides what is asked for, not what is served.
  const canDeploy = useAuthStore((s) => s.hasPermission('deploy_apps'))
  const canViewJobs = useAuthStore((s) => s.hasPermission('view_jobs'))
  const deploymentsQ = useDeploymentJobsQuery({ enabled: canDeploy })
  const packagingQ = usePackagingJobsQuery({ enabled: canViewJobs })
  const dismissed = useTaskListStore((s) => s.dismissed)
  const loadedFor = useTaskListStore((s) => s.tenantId)
  const loadDismissed = useTaskListStore((s) => s.loadDismissed)
  const prune = useTaskListStore((s) => s.prune)

  // The dismissed set follows the tenant, read before the first list lands so a dismissed run
  // is never shown for one render.
  useEffect(() => {
    if (loadedFor !== tenantId) loadDismissed(tenantId)
  }, [tenantId, loadedFor, loadDismissed])

  const deployments = deploymentsQ.data?.deployments
  const packaging = packagingQ.data?.jobs

  // Pruned to the FINISHED ids of lists that have been read. A running id is dropped on purpose:
  // a dismissed run that is retried is running again, and its next outcome has not been read. A
  // query that is disabled contributes nothing to keep, and one that has never succeeded is not
  // yet evidence that an id is gone; on a failed refresh `data` is still the last successful read,
  // so nothing is dropped on an outage.
  useEffect(() => {
    const unread =
      (deploymentsQ.isEnabled && deployments === undefined) ||
      (packagingQ.isEnabled && packaging === undefined)
    if (unread || loadedFor !== tenantId) return
    prune([
      ...(deployments ?? []).filter((j) => !isRunningDeployment(j)).map((j) => j.id),
      ...(packaging ?? []).filter((j) => !isRunningPackaging(j)).map((j) => j.job_id),
    ])
  }, [deployments, packaging, deploymentsQ.isEnabled, packagingQ.isEnabled, loadedFor, tenantId, prune])

  const view = useMemo(
    () => deriveTasks(deployments, packaging, dismissed),
    [deployments, packaging, dismissed],
  )

  const error = deploymentsQ.error ?? packagingQ.error ?? null

  return {
    ...view,
    runningCount: view.running.length,
    /** The last refresh failed; `tasks` is still the last successful read. */
    error: error ? errorText(error, 'The task list could not be refreshed.') : null,
    isFetching: deploymentsQ.isFetching || packagingQ.isFetching,
    isLoading:
      (deploymentsQ.isEnabled && deploymentsQ.isLoading) ||
      (packagingQ.isEnabled && packagingQ.isLoading),
    showsDeployments: deploymentsQ.isEnabled,
    showsPackaging: packagingQ.isEnabled,
  }
}
